import React, { useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";

interface ArtigoUsadoProps {
  titulo: string;
  link: string;
  // HTML do artigo, vindo da SAK. Pode faltar (conversa antiga ou storage compactado): aí o
  // botão abre o artigo na central da SAK.
  html?: string;
}

// Só o que um artigo de ajuda precisa: texto, listas, tabelas, imagens e links. Iframes (vídeos)
// ficam de fora porque o vídeo já aparece acima, no player da central.
const TAGS = [
  "p", "br", "h1", "h2", "h3", "h4", "strong", "b", "em", "i", "u", "ul", "ol", "li", "a", "img",
  "blockquote", "code", "pre", "table", "thead", "tbody", "tr", "th", "td", "span", "div", "hr",
];
const ATRIBUTOS = ["href", "target", "rel", "src", "alt", "title"];

const VAZIO = /<(p|div|span|strong|b|em|i|u)[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/\1>/gi;

// Blocos vazios (onde ficava o vídeo, espaços com &nbsp;) deixavam um vão no fim do artigo.
// Repete porque um bloco só fica vazio depois que os de dentro saem.
const semBlocosVazios = (html: string) => {
  let anterior = "";
  while (anterior !== html) {
    anterior = html;
    html = html.replace(VAZIO, "");
  }
  return html.trim();
};

const limpar = (html: string) =>
  semBlocosVazios(
    DOMPurify.sanitize(html, { ALLOWED_TAGS: TAGS, ALLOWED_ATTR: ATRIBUTOS })
      // links abrem em outra aba; imagens com src "//..." ganham https
      .replace(/<a\s/g, '<a target="_blank" rel="noopener noreferrer" ')
      .replace(/src="\/\//g, 'src="https://')
  );

// "Baseado no artigo: X" + o artigo completo aberto dentro do chat, sem sair da conversa.
const ArtigoUsado: React.FC<ArtigoUsadoProps> = ({ titulo, link, html }) => {
  const { t } = useTranslation();
  const [aberto, setAberto] = useState(false);
  const conteudo = useMemo(() => (aberto && html ? limpar(html) : ""), [aberto, html]);

  return (
    <div className="artigo-usado">
      <div className="artigo-usado-linha">
        <span className="artigo-usado-rotulo">
          {t("baseado_no_artigo")}: <strong>{titulo}</strong>
        </span>
        {html ? (
          <button className="artigo-usado-botao" aria-expanded={aberto} onClick={() => setAberto((a) => !a)}>
            {aberto ? t("fechar_artigo") : t("ver_artigo_completo")}
          </button>
        ) : (
          <a className="artigo-usado-botao" href={link} target="_blank" rel="noopener noreferrer">
            {t("abrir_artigo")}
          </a>
        )}
      </div>

      {aberto && (
        <div className="artigo-usado-conteudo">
          <div dangerouslySetInnerHTML={{ __html: conteudo }} />
          <a className="artigo-usado-fonte" href={link} target="_blank" rel="noopener noreferrer">
            {t("abrir_na_central")}
          </a>
        </div>
      )}
    </div>
  );
};

export default ArtigoUsado;
